<template>
  <tbody
    class="directory-order-table-row"
    :class="[index % 2 === 0 ? 'row-even' : 'row-odd']"
  >
    <template v-if="showVirtualScrollPlaceholder">
      <tr class="virtual-scroll-placeholder">
        <td colspan="12">
          <!-- inline style here to center the text `loading...`  -->
          <div :style="{display: 'inline-block', minHeight: placeHolderMinHeight + 'px', verticalAlign: 'middle'}">&nbsp;</div>
          <span class="font-dark-gray">{{ $t('loading') }}...</span>
        </td>
      </tr>
    </template>
    <template v-else>
      <template v-for="(item, dIndex) in displayItems">
        <template
          v-for="(company, mainCompanyIndex) in item.companies"
        >
          <tr
            :key="company.id"
            class="directory-row"
          >
            <td class="tenancyId">
              <a
                v-if="allowClickTenancyId"
                class="name-hyper-link"
                @click="clickTenancyId"
              >{{ mainCompanyIndex ? '' : item.tenancy.id }}</a>
              <span v-else>{{ mainCompanyIndex ? '' : item.tenancy.id }}</span>
            </td>
            <td
              class="premises"
              v-if="isFloor"
            >{{ company['premises'] | isEmptyValue }}</td>
            <td class="engName">
              <a
                v-if="allowClickCompanyName"
                class="name-hyper-link"
                @click="clickCompanyName"
              >{{ company.engName | isEmptyValue }}
              </a>
              <span v-else>{{ company.engName | isEmptyValue }}</span>
              <span
                v-if="!company.chsName && company.chtName && viewType === 'LOBBY'"
                class="no-cht-name"
              >{{ $t('directory.hint.chinese') }}</span>
            </td>
            <td class="chtName">
              {{ company.chtName | isEmptyValue }}
            </td>
            <td class="floor">
              {{ (isFloor ? (item.floor || $t('emptyValue')) : getFloorAndPremisesAtTerminal(company)) }}
            </td>
            <td class="effectiveDate">{{ mainCompanyIndex ? '' : formatEffectiveDate(directory) }}</td>
            <td
              class="screen"
              v-if="!isFloor"
            >
              <HKLSelect
                v-show="allowChangeScreen && dIndex == 0"
                class="hkl-select--smallsize"
                :disabled="!allowChangeScreen || dIndex !== 0"
                :value="item.screen"
                :options="screenList"
                :showSearch="false"
                placeholder=""
                @change="changeScreen"
              />
            </td>
            <td class="display">
              <HKLSelect
                v-show="allowChangeColumn && dIndex == 0"
                class="hkl-select--smallsize"
                :disabled="!allowChangeColumn || dIndex !== 0"
                :value="item.position"
                :options="displayList"
                :showSearch="false"
                placeholder=""
                @change="changeColumn"
              />
            </td>
            <td class="onHold">
              <input
                type="checkbox"
                v-if="allowUpdateOnHold && dIndex === 0"
                :checked="item.onHold"
                @change="event => updateOnHold(event.target.checked)"
                v-hkldinput
              >
            </td>
            <td
              class="status"
              v-show="!mainCompanyIndex"
              :class="`status--${getStatusClass(directory)}`"
            >
              <span>{{ formatStatus(item.status, item.action) }}</span>
            </td>
            <td class="order">
              <HKLSelect
                v-if="!mainCompanyIndex && allowChangeOrder && dIndex === 0"
                class="hkl-select--smallsize hkl-select--order"
                :value="item.order"
                :options="orderSelectOptions"
                :showSearch="true"
                placeholder=""
                @change="changeOrder"
              />
            </td>
            <td class="action">
              <div
                v-if="!mainCompanyIndex && allowChangeOrder && dIndex === 0"
                class="directory-actions"
              >
                <i
                  class="arrow-up"
                  @click="moveUp"
                  v-if="allowMoveUp"
                />
                <i
                  class="arrow-down"
                  @click="moveDown"
                  v-if="allowMoveDown"
                />
              </div>
            </td>
            <td class="action">
              <dotted-dropdown
                :id="`dottedDropdown-${item.id}`"
                v-if="showDotDropDown && dotOptions.length && !mainCompanyIndex && dIndex === 0"
              >
                <li>
                  <a
                    v-for="(action, index) in dotOptions"
                    :key="index"
                    class="dot-option-btn"
                    @click="action.method"
                  >{{ action.name }}</a>
                </li>
              </dotted-dropdown>
            </td>
          </tr>
          <template v-for="subComp in company.subCompanies">
            <tr
              :key="subComp.id"
              class="sub-company directory-row"
            >
              <td class="tenancyId" />
              <td
                class="premises"
                v-if="isFloor"
              >{{ subComp['premises'] }}</td>
              <td
                v-for="header in subDetailHeaders"
                :key="header"
                :class="header.cls"
              >{{
                 typeof header.attr === 'function'
                   ? header.attr(subComp)
                   : (subComp[header.attr] || $i18n.t('emptyValue'))
               }}
                <span
                  v-if="!subComp.chsName && header.attr === 'engName' && subComp.chtName && viewType === 'LOBBY'"
                  class="no-cht-name"
                >{{ $t('directory.hint.chinese') }}</span>
              </td>
              <td colspan="8" />
            </tr>
            <tr
              :key="detail.id"
              v-for="detail in subComp.subDetails"
              class="sub-detail directory-row"
            >
              <td class="tenancyId" />
              <!-- there should be no premises info for level3/detail -->
              <td
                class="premises"
                v-if="isFloor"
              />
              <td
                v-for="header in subDetailHeaders"
                :key="header"
                :class="header.cls"
              >{{
                typeof header.attr === 'function'
                  ? header.attr(detail)
                  : (detail[header.attr] || $i18n.t('emptyValue'))
              }}
              </td>
              <td colspan="8" />
            </tr>
          </template>
        </template>
      </template>
    </template>
  </tbody>
</template>
<script>
import lodash from 'lodash'
import { DIR_TYPE, DIR_LOBBY_SCREENS, DIR_FLOOR_DISPLAYS, DIR_LOBBY_DISPLAYS, DIR_STATUS } from './consts'
import { getFloorAndPremisesAtTerminal, getSubDetailHeaders } from './directoryOrderTableHelper'
import { formatEffectiveDate, isPublishedInMaintain } from './utils'

import HKLSelect from '@/controls/Select/HKLSelect.vue'
import DottedDropdown from '@/controls/DottedDropdown'

const VIRTUAL_SCROLL_ITEM_DEFAULT_MIN_HEIGHT = 40

export default {
  components: {
    HKLSelect,
    DottedDropdown,
  },
  props: {
    viewType: {
      type: String,
      required: true,
    },
    directory: {
      type: Object,
      required: true,
    },
    displayItems: {
      type: Array,
      default: [],
    },
    index: {
      type: Number,
      required: true,
    },
    orderOptions: {
      type: Array,
      default: [],
    },
    actionSettings: {
      type: Object,
      default: {},
    },
    windowScrollY: {
      type: Number,
      required: true,
    },
  },
  data () {
    return {
      subDetailHeaders: getSubDetailHeaders(this.viewType),
      DIR_TYPE,
      lodash,
      placeHolderMinHeightCache: VIRTUAL_SCROLL_ITEM_DEFAULT_MIN_HEIGHT,
    }
  },
  methods: {
    formatEffectiveDate,
    getFloorAndPremisesAtTerminal,
    formatStatus (status, action) {
      if (isPublishedInMaintain(status, action)) {
        return DIR_STATUS.PUBLISHED
      } else {
        return `${status} (${action})`
      }
    },
    getStatusClass (item) {
      if (isPublishedInMaintain(item.status, item.action)) {
        return DIR_STATUS.PUBLISHED.toLowerCase()
      } else {
        return item.status.toLowerCase()
      }
    },
  },
  filters: {
    capitalize (val) {
      if (!val) return ''
      val = val.toString()
      return val === 'n/a' ? 'N/A' : val.charAt(0).toUpperCase() + val.slice(1)
    },
  },
  computed: {
    clientRectInfo () {
      // windowScrollY > -1 is always true, put here to trigger re-calculating when windowScrollY changes
      if (this.windowScrollY > -1 && this.$el) {
        const {top, height, bottom} = this.$el.getBoundingClientRect()
        return {
          top: Math.floor(top),
          bottom: Math.floor(bottom),
          height: Math.floor(height),
        }
      }
    },
    placeHolderMinHeight () {
      const {height = VIRTUAL_SCROLL_ITEM_DEFAULT_MIN_HEIGHT} = this.clientRectInfo || {}

      // minimum change check to prevent mis-calculating in Chrome
      this.placeHolderMinHeightCache = (Math.abs(height - this.placeHolderMinHeightCache) < 3)
        ? this.placeHolderMinHeightCache
        : Math.max(height, VIRTUAL_SCROLL_ITEM_DEFAULT_MIN_HEIGHT)

      return this.placeHolderMinHeightCache
    },
    showVirtualScrollPlaceholder () {
      if (!this.displayItems.length) return false

      const rect = this.clientRectInfo
      const noRectInfo = !rect || (rect.top === 0 && rect.height === 0)

      // items in initial first page
      if (this.windowScrollY === 0 || noRectInfo) {
        const initialDisplayAreaHeight = window.innerHeight - 400
        return this.index > initialDisplayAreaHeight / VIRTUAL_SCROLL_ITEM_DEFAULT_MIN_HEIGHT
      }

      // items in viewport ± buffer
      const buffer = window.innerHeight
      const outOfViewportWithBuffer = (rect.top > window.innerHeight + buffer) || (rect.bottom < -buffer)
      return outOfViewportWithBuffer
    },
    isFloor () {
      return this.viewType === DIR_TYPE.FLOOR
    },
    screenList () {
      return DIR_LOBBY_SCREENS
    },
    displayList () {
      return this.isFloor ? DIR_FLOOR_DISPLAYS : DIR_LOBBY_DISPLAYS
    },
    orderSelectOptions () {
      return (this.orderOptions || []).map((order) => ({
        name: String(order),
        value: order,
      }))
    },
    changeColumn () {
      return lodash.get(this.actionSettings, 'changeColumn.action')
    },
    changeScreen () {
      return lodash.get(this.actionSettings, 'changeScreen.action')
    },
    moveUp () {
      return lodash.get(this.actionSettings, 'moveUp.action')
    },
    moveDown () {
      return lodash.get(this.actionSettings, 'moveDown.action')
    },
    changeOrder () {
      return lodash.get(this.actionSettings, 'changeOrder.action')
    },
    updateOnHold () {
      return lodash.get(this.actionSettings, 'updateOnHold.action')
    },
    clickTenancyId () {
      return lodash.get(this.actionSettings, 'clickTenancyId.action')
    },
    clickCompanyName () {
      return lodash.get(this.actionSettings, 'clickCompanyName.action')
    },
    dotOptions () {
      return lodash.get(this.actionSettings, 'dotOptions.actions')
    },
    allowClickTenancyId () {
      return lodash.get(this.actionSettings, 'clickTenancyId.enabled')
    },
    allowClickCompanyName () {
      return lodash.get(this.actionSettings, 'clickCompanyName.enabled')
    },
    allowChangeScreen () {
      return lodash.get(this.actionSettings, 'changeScreen.enabled')
    },
    allowChangeColumn () {
      return lodash.get(this.actionSettings, 'changeColumn.enabled')
    },
    allowChangeOrder () {
      return lodash.get(this.actionSettings, 'changeOrder.enabled')
    },
    allowUpdateOnHold () {
      return lodash.get(this.actionSettings, 'updateOnHold.enabled')
    },
    allowMoveUp () {
      return lodash.get(this.actionSettings, 'moveUp.enabled')
    },
    allowMoveDown () {
      return lodash.get(this.actionSettings, 'moveDown.enabled')
    },
    showDotDropDown () {
      return lodash.get(this.actionSettings, 'dotOptions.enabled')
    },
  },
}
</script>
<style lang="scss" scoped>
@import "~styles/table";
@import "~styles/directories/maintain";
@import "~styles/directories/common";

.row-odd {
  tr {
    background-color: #fefefe !important;
  }
}
.row-even {
  tr {
    background-color: #f8f8f8 !important;
  }
}
.virtual-scroll-placeholder {
  td {
    text-align: center;
    padding: 0;
  }
}
.name-hyper-link {
  color: #1779ba;
}
</style>
